/**
 * バリデーション用のユーティリティ関数
 */

import * as pdfjsLib from 'pdfjs-dist';
import pdfjsWorker from 'pdfjs-dist/build/pdf.worker.entry';

// シナリオのバリデーション定数
export const SCENARIO_VALIDATIONS = {
  TITLE: {
    MIN_LENGTH: 1,
    MAX_LENGTH: 260,
    ONLY_ALPHANUMERIC: false,
    REQUIRED: true
  },
  DESCRIPTION: {
    MIN_LENGTH: 1,
    MAX_LENGTH: 1000,
    ONLY_ALPHANUMERIC: false,
    REQUIRED: true
  },
  GOAL: {
    MIN_LENGTH: 1,
    MAX_LENGTH: 260,
    ONLY_ALPHANUMERIC: false,
    REQUIRED: true
  },
  ESTIMATED_TIME: {
    MIN_LENGTH: 1,
    MAX_LENGTH: 3,
    MIN_VALUE: 1,
    MAX_VALUE: 30,
    ONLY_NUMERIC: true,
    REQUIRED: true
  },
  SITUATION: {
    MIN_LENGTH: 1,
    MAX_LENGTH: 2000,
    ONLY_ALPHANUMERIC: false,
    REQUIRED: true
  },
  /** 状況設定（クローズ）：2,000文字上限・非必須 */
  SITUATION_CLOSED: {
    MIN_LENGTH: 0,
    MAX_LENGTH: 2000,
    ONLY_ALPHANUMERIC: false,
    REQUIRED: false
  },
  TARGET_NAME: {
    MIN_LENGTH: 1,
    MAX_LENGTH: 200,
    ONLY_ALPHANUMERIC: false,
    REQUIRED: true
  },
  DEPARTMENT: {
    MIN_LENGTH: 0,
    MAX_LENGTH: 128,
    ONLY_ALPHANUMERIC: false,
    CAN_UNDERSCORE: true,
    CAN_HYPHEN: true,
    CAN_WHITESPACE: true,
    REQUIRED: false
  },
};

// カスタム評価ルールのバリデーション定数
export const CUSTOM_EVALUATION_RULE_VALIDATIONS = {
  NAME: {
    MIN_LENGTH: 1,
    MAX_LENGTH: 260,
    ONLY_ALPHANUMERIC: false,
    REQUIRED: true
  },
  CONTENT: {
    MIN_LENGTH: 1,
    MAX_LENGTH: 2000,
    ONLY_ALPHANUMERIC: false,
    REQUIRED: true
  },
};

/**
 * 問い合わせフォームの名前のバリデーションを行う
 * @param name 名前
 * @returns バリデーション結果（成功時はtrue、失敗時はエラーメッセージ）
 */
export const validateInquiryName = (name: string): { isValid: boolean; errorMessage?: string } => {
  const validation = INQUIRY_VALIDATIONS.NAME;
  
  // 必須チェック
  if (validation.REQUIRED && !name.trim()) {
    return {
      isValid: false,
      errorMessage: 'お名前は必須です'
    };
  }
  
  // 長さチェック
  if (name.trim() && name.length < validation.MIN_LENGTH) {
    return {
      isValid: false,
      errorMessage: `お名前は${validation.MIN_LENGTH}文字以上である必要があります`
    };
  }
  
  if (name.length > validation.MAX_LENGTH) {
    return {
      isValid: false,
      errorMessage: `お名前は${validation.MAX_LENGTH}文字以下である必要があります`
    };
  }
  
  return { isValid: true };
};

/**
 * 問い合わせフォームのメールアドレスのバリデーションを行う
 * @param email メールアドレス
 * @returns バリデーション結果（成功時はtrue、失敗時はエラーメッセージ）
 */
export const validateInquiryEmail = (email: string): { isValid: boolean; errorMessage?: string } => {
  const validation = INQUIRY_VALIDATIONS.EMAIL;
  
  // 必須チェック
  if (validation.REQUIRED && !email.trim()) {
    return {
      isValid: false,
      errorMessage: 'メールアドレスは必須です'
    };
  }
  
  // 長さチェック
  if (email.trim() && email.length < validation.MIN_LENGTH) {
    return {
      isValid: false,
      errorMessage: `メールアドレスは${validation.MIN_LENGTH}文字以上である必要があります`
    };
  }
  
  if (email.length > validation.MAX_LENGTH) {
    return {
      isValid: false,
      errorMessage: `メールアドレスは${validation.MAX_LENGTH}文字以下である必要があります`
    };
  }
  
  // メールアドレス形式チェック
  if (email.trim() && validation.REQUIRE_EMAIL_FORMAT) {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      return {
        isValid: false,
        errorMessage: 'メールアドレスの形式が正しくありません'
      };
    }
  }
  
  return { isValid: true };
};

/**
 * 問い合わせフォームのメッセージのバリデーションを行う
 * @param message メッセージ
 * @returns バリデーション結果（成功時はtrue、失敗時はエラーメッセージ）
 */
export const validateInquiryMessage = (message: string): { isValid: boolean; errorMessage?: string } => {
  const validation = INQUIRY_VALIDATIONS.MESSAGE;
  
  // 必須チェック
  if (validation.REQUIRED && !message.trim()) {
    return {
      isValid: false,
      errorMessage: 'お問い合わせ内容は必須です'
    };
  }
  
  // 長さチェック
  if (message.trim() && message.length < validation.MIN_LENGTH) {
    return {
      isValid: false,
      errorMessage: `お問い合わせ内容は${validation.MIN_LENGTH}文字以上である必要があります`
    };
  }
  
  if (message.length > validation.MAX_LENGTH) {
    return {
      isValid: false,
      errorMessage: `お問い合わせ内容は${validation.MAX_LENGTH}文字以下である必要があります`
    };
  }
  
  return { isValid: true };
};

/**
 * 問い合わせフォーム全体のバリデーションを行う
 * @param inquiry 問い合わせ情報
 * @returns バリデーション結果（成功時は空の配列、失敗時はエラーメッセージの配列）
 */
export const validateInquiry = (
  inquiry: {
    name: string;
    email: string;
    message: string;
  }
): { isValid: boolean; errors: { field: string; message: string }[] } => {
  const errors: { field: string; message: string }[] = [];
  
  // 名前
  const nameResult = validateInquiryName(inquiry.name);
  if (!nameResult.isValid) {
    errors.push({
      field: 'name',
      message: nameResult.errorMessage || 'お名前が無効です'
    });
  }
  
  // メールアドレス
  const emailResult = validateInquiryEmail(inquiry.email);
  if (!emailResult.isValid) {
    errors.push({
      field: 'email',
      message: emailResult.errorMessage || 'メールアドレスが無効です'
    });
  }
  
  // メッセージ
  const messageResult = validateInquiryMessage(inquiry.message);
  if (!messageResult.isValid) {
    errors.push({
      field: 'message',
      message: messageResult.errorMessage || 'お問い合わせ内容が無効です'
    });
  }
  
  return {
    isValid: errors.length === 0,
    errors
  };
};

// PDFファイルのバリデーション定数
export const PDF_VALIDATIONS = {
  MAX_FILES: 10,
  MAX_FILE_SIZE: 10 * 1024 * 1024, // 10MB
  MAX_FILE_SIZE_MB: () => (PDF_VALIDATIONS.MAX_FILE_SIZE / (1024 * 1024)).toFixed(2),
  ALLOWED_FILE_TYPE: 'application/pdf',
  MAX_TEXT_LENGTH: 20000 // 2万文字
};

// ユーザーのバリデーション定数
export const USER_VALIDATIONS = {
  USER_ID: {
    MIN_LENGTH: 1,
    MAX_LENGTH: 40,
    ONLY_ALPHANUMERIC: true,
    CAN_UNDERSCORE: true,
    CAN_HYPHEN: true,
    CAN_DOT: true,
    CAN_WHITESPACE: false,
    REQUIRED: true
  },
  PASSWORD: {
    MIN_LENGTH: 8,
    MAX_LENGTH: 128,
    CONTAINS_NUMBER: true,
    CONTAINS_UPPERCASE: true,
    CONTAINS_LOWERCASE: true,
    CONTAINS_SYMBOL: true,
    REQUIRED: true
  },
  DISPLAY_NAME: {
    MIN_LENGTH: 1,
    MAX_LENGTH: 128,
    ONLY_ALPHANUMERIC: false,
    CAN_UNDERSCORE: true,
    CAN_HYPHEN: true,
    CAN_WHITESPACE: true,
    REQUIRED: true
  },
  EMAIL: {
    MIN_LENGTH: 0,
    MAX_LENGTH: 256,
    ONLY_ALPHANUMERIC: false,
    CAN_UNDERSCORE: true,
    CAN_HYPHEN: true,
    CAN_WHITESPACE: false,
    REQUIRE_EMAIL_FORMAT: true,
    REQUIRED: false
  },
  DEPARTMENT: {
    MIN_LENGTH: 0,
    MAX_LENGTH: 128,
    ONLY_ALPHANUMERIC: false,
    CAN_UNDERSCORE: true,
    CAN_HYPHEN: true,
    CAN_WHITESPACE: true,
    REQUIRED: false
  },
  USER_ROLE: {
    ALLOWED_VALUES: ['ADMIN', 'USER'],
    REQUIRED: true
  },
  USER_STATUS: {
    ALLOWED_VALUES: [true, false],
    REQUIRED: true
  }
};

// 問合せのバリデーション定数
export const INQUIRY_VALIDATIONS = {
  NAME: {
    MIN_LENGTH: 1,
    MAX_LENGTH: 128,
    ONLY_ALPHANUMERIC: false,
    CAN_UNDERSCORE: true,
    CAN_HYPHEN: true,
    CAN_WHITESPACE: true,
    REQUIRED: true
  },
  EMAIL: {
    MIN_LENGTH: 1,
    MAX_LENGTH: 256,
    ONLY_ALPHANUMERIC: false,
    CAN_UNDERSCORE: true,
    CAN_HYPHEN: true,
    CAN_WHITESPACE: false,
    REQUIRE_EMAIL_FORMAT: true,
    REQUIRED: true
  },
  MESSAGE: {
    MIN_LENGTH: 1,
    MAX_LENGTH: 2000,
    ONLY_ALPHANUMERIC: false,
    CAN_UNDERSCORE: true,
    CAN_HYPHEN: true,
    CAN_WHITESPACE: true,
    REQUIRED: true
  }
};

// PDF.jsのワーカーを設定
try {
  pdfjsLib.GlobalWorkerOptions.workerSrc = pdfjsWorker;
  console.log('PDF.js worker set using Webpack bundle');
} catch (error) {
  console.error('PDF.jsワーカーの設定中にエラーが発生しました:', error);
}

/**
 * パスワードがポリシーに準拠しているかチェックする
 * @param password チェック対象のパスワード
 * @returns バリデーション結果（成功時はtrue、失敗時はエラーメッセージ）
 */
export const validatePassword = (password: string): { isValid: boolean; errorMessage?: string } => {
  // パスワードの長さをチェック
  if (password.length < USER_VALIDATIONS.PASSWORD.MIN_LENGTH) {
    return {
      isValid: false,
      errorMessage: `パスワードは${USER_VALIDATIONS.PASSWORD.MIN_LENGTH}文字以上である必要があります`
    };
  }

  // 最大長のチェック
  if (password.length > USER_VALIDATIONS.PASSWORD.MAX_LENGTH) {
    return {
      isValid: false,
      errorMessage: `パスワードは${USER_VALIDATIONS.PASSWORD.MAX_LENGTH}文字以下である必要があります`
    };
  }

  // 各条件のチェック
  const validationChecks = [
    { condition: USER_VALIDATIONS.PASSWORD.CONTAINS_NUMBER, test: /\d/.test(password), message: '数字' },
    { condition: USER_VALIDATIONS.PASSWORD.CONTAINS_UPPERCASE, test: /[A-Z]/.test(password), message: '大文字' },
    { condition: USER_VALIDATIONS.PASSWORD.CONTAINS_LOWERCASE, test: /[a-z]/.test(password), message: '小文字' },
    { condition: USER_VALIDATIONS.PASSWORD.CONTAINS_SYMBOL, test: /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password), message: '記号' }
  ];

  // 必要な条件をフィルタリングして、満たしていない条件があるかチェック
  const failedChecks = validationChecks
    .filter(check => check.condition && !check.test)
    .map(check => check.message);

  if (failedChecks.length > 0) {
    return {
      isValid: false,
      errorMessage: `パスワードには${failedChecks.join('、')}を含める必要があります`
    };
  }

  // すべての条件を満たしている場合
  return { isValid: true };
};

/**
 * パスワードポリシーの説明文を取得する
 * @returns パスワードポリシーの説明文
 */
export const getPasswordPolicyDescription = (): string => {
  const requirements = [];
  
  // 長さの要件
  requirements.push(`${USER_VALIDATIONS.PASSWORD.MIN_LENGTH}～${USER_VALIDATIONS.PASSWORD.MAX_LENGTH}文字`);
  
  // その他の要件
  const contentRequirements = [];
  if (USER_VALIDATIONS.PASSWORD.CONTAINS_NUMBER) contentRequirements.push('数字');
  if (USER_VALIDATIONS.PASSWORD.CONTAINS_UPPERCASE) contentRequirements.push('大文字');
  if (USER_VALIDATIONS.PASSWORD.CONTAINS_LOWERCASE) contentRequirements.push('小文字');
  if (USER_VALIDATIONS.PASSWORD.CONTAINS_SYMBOL) contentRequirements.push('記号');
  
  if (contentRequirements.length > 0) {
    requirements.push(contentRequirements.join('、'));
  }
  
  return `パスワードは${requirements.join('で、')}を含む必要があります。`;
};

/**
 * PDFファイルのバリデーションを行う
 * @param files バリデーション対象のファイル配列
 * @param currentFiles 既に選択されているファイル配列（オプション）
 * @returns バリデーション結果（成功時はnull、失敗時はエラーメッセージ）
 */
export const validatePdfFiles = (files: File[], currentFiles: File[] = []): string | null => {
  // 1. ファイル形式のチェック（PDFのみ）
  const invalidTypeFiles = files.filter(file => file.type !== PDF_VALIDATIONS.ALLOWED_FILE_TYPE);
  if (invalidTypeFiles.length > 0) {
    return `PDFファイルのみアップロード可能です。（${invalidTypeFiles.map(f => f.name).join(', ')}）`;
  }
  
  // 2. ファイルサイズのチェック（10MB以下）
  const oversizedFiles = files.filter(file => file.size > PDF_VALIDATIONS.MAX_FILE_SIZE);
  if (oversizedFiles.length > 0) {
    return `ファイルサイズは10MB以下にしてください。（${oversizedFiles.map(f => f.name).join(', ')}）`;
  }
  
  // 3. ファイル数のチェック（最大10件）
  if (currentFiles.length + files.length > PDF_VALIDATIONS.MAX_FILES) {
    return `添付できるファイルは最大${PDF_VALIDATIONS.MAX_FILES}件です。`;
  }
  
  // すべての条件を満たしている場合
  return null;
};

/**
 * PDFからテキストを抽出する関数
 * @param file PDFファイル
 * @returns 抽出されたテキスト
 */
export const extractTextFromPdf = async (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    const fileReader = new FileReader();
    
    fileReader.onload = async (event) => {
      try {
        const typedArray = new Uint8Array(event.target?.result as ArrayBuffer);
        const loadingTask = pdfjsLib.getDocument({ data: typedArray });
        const pdf = await loadingTask.promise;
        
        let fullText = '';
        
        // 全ページからテキストを抽出
        for (let i = 1; i <= pdf.numPages; i++) {
          const page = await pdf.getPage(i);
          const textContent = await page.getTextContent();
          const pageText = textContent.items.map((item: any) => item.str).join(' ');
          fullText += pageText + ' ';
          
          // 文字数制限を超えた場合は早期リターン
          if (fullText.length > PDF_VALIDATIONS.MAX_TEXT_LENGTH) {
            resolve(fullText);
            return;
          }
        }
        
        resolve(fullText);
      } catch (error) {
        console.error('PDFからテキストを抽出中にエラーが発生しました:', error);
        reject(error);
      }
    };
    
    fileReader.onerror = (error) => {
      console.error('ファイル読み込み中にエラーが発生しました:', error);
      reject(error);
    };
    
    fileReader.readAsArrayBuffer(file);
  });
};

/**
 * PDFの文字数をチェックする関数
 * @param text PDFから抽出されたテキスト
 * @param fileName ファイル名（エラーメッセージ用）
 * @returns バリデーション結果（成功時はnull、失敗時はエラーメッセージ）
 */
export const validatePdfTextLength = (text: string, fileName: string): string | null => {
  console.log(`PDFファイル "${fileName}" の文字数: ${text.length}`);
  if (text.length > PDF_VALIDATIONS.MAX_TEXT_LENGTH) {
    return `ファイル "${fileName}" の文字数が多すぎます。${PDF_VALIDATIONS.MAX_TEXT_LENGTH}文字以下のPDFを選択してください。`;
  }
  return null;
};

/**
 * ユーザーIDのバリデーションを行う
 * @param userId ユーザーID
 * @returns バリデーション結果（成功時はtrue、失敗時はエラーメッセージ）
 */
export const validateUserId = (userId: string): { isValid: boolean; errorMessage?: string } => {
  const validation = USER_VALIDATIONS.USER_ID;
  
  // 必須チェック
  if (validation.REQUIRED && !userId) {
    return {
      isValid: false,
      errorMessage: 'ユーザーIDは必須です'
    };
  }
  
  // 長さチェック
  if (userId.length < validation.MIN_LENGTH) {
    return {
      isValid: false,
      errorMessage: `ユーザーIDは${validation.MIN_LENGTH}文字以上である必要があります`
    };
  }
  
  if (userId.length > validation.MAX_LENGTH) {
    return {
      isValid: false,
      errorMessage: `ユーザーIDは${validation.MAX_LENGTH}文字以下である必要があります`
    };
  }
  
  // 文字種チェック
  let pattern = '';
  if (validation.ONLY_ALPHANUMERIC) {
    pattern = '^[a-zA-Z0-9';
    if (validation.CAN_UNDERSCORE) pattern += '_';
    if (validation.CAN_HYPHEN) pattern += '\\-';
    if (validation.CAN_DOT) pattern += '\\.';
    if (validation.CAN_WHITESPACE) pattern += '\\s';
    pattern += ']+$';
    
    const regex = new RegExp(pattern);
    if (!regex.test(userId)) {
      let allowedChars = '英数字';
      if (validation.CAN_UNDERSCORE) allowedChars += '、アンダースコア(_)';
      if (validation.CAN_HYPHEN) allowedChars += '、ハイフン(-)';
      if (validation.CAN_DOT) allowedChars += '、ピリオド(.)';
      if (validation.CAN_WHITESPACE) allowedChars += '、空白';
      
      return {
        isValid: false,
        errorMessage: `ユーザーIDは${allowedChars}のみ使用できます`
      };
    }
  }
  
  return { isValid: true };
};

/**
 * 表示名のバリデーションを行う
 * @param displayName 表示名
 * @returns バリデーション結果（成功時はtrue、失敗時はエラーメッセージ）
 */
export const validateDisplayName = (displayName: string): { isValid: boolean; errorMessage?: string } => {
  const validation = USER_VALIDATIONS.DISPLAY_NAME;
  
  // 必須チェック
  if (validation.REQUIRED && !displayName) {
    return {
      isValid: false,
      errorMessage: '氏名は必須です'
    };
  }
  
  // 長さチェック
  if (displayName && displayName.length < validation.MIN_LENGTH) {
    return {
      isValid: false,
      errorMessage: `氏名は${validation.MIN_LENGTH}文字以上である必要があります`
    };
  }
  
  if (displayName && displayName.length > validation.MAX_LENGTH) {
    return {
      isValid: false,
      errorMessage: `氏名は${validation.MAX_LENGTH}文字以下である必要があります`
    };
  }
  
  // 文字種チェック
  if (displayName && validation.ONLY_ALPHANUMERIC) {
    let pattern = '^[a-zA-Z0-9';
    if (validation.CAN_UNDERSCORE) pattern += '_';
    if (validation.CAN_HYPHEN) pattern += '-';
    if (validation.CAN_WHITESPACE) pattern += '\\s';
    pattern += ']+$';
    
    const regex = new RegExp(pattern);
    if (!regex.test(displayName)) {
      let allowedChars = '英数字';
      if (validation.CAN_UNDERSCORE) allowedChars += '、アンダースコア(_)';
      if (validation.CAN_HYPHEN) allowedChars += '、ハイフン(-)';
      if (validation.CAN_WHITESPACE) allowedChars += '、空白';
      
      return {
        isValid: false,
        errorMessage: `氏名は${allowedChars}のみ使用できます`
      };
    }
  }
  
  return { isValid: true };
};

/**
 * メールアドレスのバリデーションを行う
 * @param email メールアドレス
 * @returns バリデーション結果（成功時はtrue、失敗時はエラーメッセージ）
 */
export const validateEmail = (email: string): { isValid: boolean; errorMessage?: string } => {
  const validation = USER_VALIDATIONS.EMAIL;
  
  // 必須チェック
  if (validation.REQUIRED && !email) {
    return {
      isValid: false,
      errorMessage: 'メールアドレスは必須です'
    };
  }
  
  // 空の場合は必須でなければOK
  if (!email && !validation.REQUIRED) {
    return { isValid: true };
  }
  
  // 長さチェック
  if (email && email.length > validation.MAX_LENGTH) {
    return {
      isValid: false,
      errorMessage: `メールアドレスは${validation.MAX_LENGTH}文字以下である必要があります`
    };
  }
  
  // メールアドレス形式チェック
  if (email && validation.REQUIRE_EMAIL_FORMAT) {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      return {
        isValid: false,
        errorMessage: 'メールアドレスの形式が正しくありません'
      };
    }
  }
  
  return { isValid: true };
};

/**
 * 部署のバリデーションを行う
 * @param department 部署
 * @returns バリデーション結果（成功時はtrue、失敗時はエラーメッセージ）
 */
export const validateDepartment = (department: string): { isValid: boolean; errorMessage?: string } => {
  const validation = USER_VALIDATIONS.DEPARTMENT;
  
  // 必須チェック
  if (validation.REQUIRED && !department) {
    return {
      isValid: false,
      errorMessage: '所属は必須です'
    };
  }
  
  // 空の場合は必須でなければOK
  if (!department && !validation.REQUIRED) {
    return { isValid: true };
  }
  
  // 長さチェック
  if (department && department.length > validation.MAX_LENGTH) {
    return {
      isValid: false,
      errorMessage: `所属は${validation.MAX_LENGTH}文字以下である必要があります`
    };
  }
  
  // 文字種チェック
  if (department && validation.ONLY_ALPHANUMERIC) {
    let pattern = '^[a-zA-Z0-9';
    if (validation.CAN_UNDERSCORE) pattern += '_';
    if (validation.CAN_HYPHEN) pattern += '-';
    if (validation.CAN_WHITESPACE) pattern += '\\s';
    pattern += ']+$';
    
    const regex = new RegExp(pattern);
    if (!regex.test(department)) {
      let allowedChars = '英数字';
      if (validation.CAN_UNDERSCORE) allowedChars += '、アンダースコア(_)';
      if (validation.CAN_HYPHEN) allowedChars += '、ハイフン(-)';
      if (validation.CAN_WHITESPACE) allowedChars += '、空白';
      
      return {
        isValid: false,
        errorMessage: `所属は${allowedChars}のみ使用できます`
      };
    }
  }
  
  return { isValid: true };
};

/**
 * ユーザー権限のバリデーションを行う
 * @param userRole ユーザー権限
 * @returns バリデーション結果（成功時はtrue、失敗時はエラーメッセージ）
 */
export const validateUserRole = (userRole: string): { isValid: boolean; errorMessage?: string } => {
  const validation = USER_VALIDATIONS.USER_ROLE;
  
  // 必須チェック
  if (validation.REQUIRED && !userRole) {
    return {
      isValid: false,
      errorMessage: '権限は必須です'
    };
  }
  
  // 許可された値かチェック
  if (userRole && !validation.ALLOWED_VALUES.includes(userRole)) {
    return {
      isValid: false,
      errorMessage: `権限は${validation.ALLOWED_VALUES.join('または')}のいずれかである必要があります`
    };
  }
  
  return { isValid: true };
};

/**
 * ユーザーステータスのバリデーションを行う
 * @param userStatus ユーザーステータス
 * @returns バリデーション結果（成功時はtrue、失敗時はエラーメッセージ）
 */
export const validateUserStatus = (userStatus: boolean|undefined): { isValid: boolean; errorMessage?: string } => {
  const validation = USER_VALIDATIONS.USER_STATUS;
  
  // 必須チェック
  if (validation.REQUIRED && userStatus === undefined) {
    return {
      isValid: false,
      errorMessage: 'ステータスは必須です'
    };
  }
  
  // 許可された値かチェック
  if (userStatus !== undefined && !validation.ALLOWED_VALUES.includes(userStatus)) {
    return {
      isValid: false,
      errorMessage: `ステータスは${validation.ALLOWED_VALUES.join('または')}のいずれかである必要があります`
    };
  }
  
  return { isValid: true };
};

/**
 * ユーザー情報全体のバリデーションを行う
 * @param user ユーザー情報
 * @param validatePasswordField パスワードのバリデーションを行うかどうか
 * @returns バリデーション結果（成功時は空の配列、失敗時はエラーメッセージの配列）
 */
export const validateUser = (
  user: {
    givenUserId?: string;
    displayName?: string;
    email?: string;
    department?: string;
    userRole?: string;
    disable?: boolean;
    tempPassword?: string;
  },
  validatePasswordField: boolean = false
): { isValid: boolean; errors: { field: string; message: string }[] } => {
  const errors: { field: string; message: string }[] = [];
  
  // ユーザーID
  if (user.givenUserId !== undefined) {
    const userIdResult = validateUserId(user.givenUserId);
    if (!userIdResult.isValid) {
      errors.push({
        field: 'givenUserId',
        message: userIdResult.errorMessage || 'ユーザーIDが無効です'
      });
    }
  }
  
  // 表示名
  if (user.displayName !== undefined) {
    const displayNameResult = validateDisplayName(user.displayName);
    if (!displayNameResult.isValid) {
      errors.push({
        field: 'displayName',
        message: displayNameResult.errorMessage || '氏名が無効です'
      });
    }
  }
  
  // メールアドレス
  if (user.email !== undefined) {
    const emailResult = validateEmail(user.email);
    if (!emailResult.isValid) {
      errors.push({
        field: 'email',
        message: emailResult.errorMessage || 'メールアドレスが無効です'
      });
    }
  }
  
  // 部署
  if (user.department !== undefined) {
    const departmentResult = validateDepartment(user.department);
    if (!departmentResult.isValid) {
      errors.push({
        field: 'department',
        message: departmentResult.errorMessage || '所属が無効です'
      });
    }
  }
  
  // ユーザー権限
  if (user.userRole !== undefined) {
    const userRoleResult = validateUserRole(user.userRole);
    if (!userRoleResult.isValid) {
      errors.push({
        field: 'userRole',
        message: userRoleResult.errorMessage || '権限が無効です'
      });
    }
  }
  
  // ユーザーステータス
  const userStatus = user.disable;
  if (userStatus !== undefined) {
    const userStatusResult = validateUserStatus(userStatus);
    if (!userStatusResult.isValid) {
      errors.push({
        field: 'disable',
        message: userStatusResult.errorMessage || 'ステータスが無効です'
      });
    }
  }
  
  // パスワード
  if (validatePasswordField && user.tempPassword !== undefined) {
    const passwordResult = validatePassword(user.tempPassword);
    if (!passwordResult.isValid) {
      errors.push({
        field: 'tempPassword',
        message: passwordResult.errorMessage || 'パスワードが無効です'
      });
    }
  }
  
  return {
    isValid: errors.length === 0,
    errors
  };
};

/**
 * シナリオ情報のバリデーションを行う
 * @param scenario シナリオ情報
 * @returns バリデーション結果（成功時は空の配列、失敗時はエラーメッセージの配列）
 */
export const validateScenario = (
  scenario: {
    title?: string;
    description?: string;
    goal?: string;
    estimatedTime?: number;
    situation?: string;
    situationClosed?: string;
    targetName?: string;
    department?: string;
  }
): { isValid: boolean; errors: { field: string; message: string }[] } => {
  const errors: { field: string; message: string }[] = [];
  
  // タイトル
  if (scenario.title !== undefined) {
    const validation = SCENARIO_VALIDATIONS.TITLE;
    
    // 必須チェック
    if (validation.REQUIRED && !scenario.title.trim()) {
      errors.push({
        field: 'title',
        message: 'タイトルは必須です'
      });
    } else if (scenario.title.trim()) {
      // 長さチェック
      if (scenario.title.length < validation.MIN_LENGTH) {
        errors.push({
          field: 'title',
          message: `タイトルは${validation.MIN_LENGTH}文字以上である必要があります`
        });
      }
      
      if (scenario.title.length > validation.MAX_LENGTH) {
        errors.push({
          field: 'title',
          message: `タイトルは${validation.MAX_LENGTH}文字以下である必要があります`
        });
      }
    }
  }
  
  // 説明文
  if (scenario.description !== undefined) {
    const validation = SCENARIO_VALIDATIONS.DESCRIPTION;
    
    // 必須チェック
    if (validation.REQUIRED && !scenario.description.trim()) {
      errors.push({
        field: 'description',
        message: 'ロープレの概要文は必須です'
      });
    } else if (scenario.description.trim()) {
      // 長さチェック
      if (scenario.description.length < validation.MIN_LENGTH) {
        errors.push({
          field: 'description',
          message: `ロープレの概要文は${validation.MIN_LENGTH}文字以上である必要があります`
        });
      }
      
      if (scenario.description.length > validation.MAX_LENGTH) {
        errors.push({
          field: 'description',
          message: `ロープレの概要文は${validation.MAX_LENGTH}文字以下である必要があります`
        });
      }
    }
  }
  
  // 目的
  if (scenario.goal !== undefined) {
    const validation = SCENARIO_VALIDATIONS.GOAL;
    
    // 必須チェック
    if (validation.REQUIRED && !scenario.goal.trim()) {
      errors.push({
        field: 'goal',
        message: 'ロープレの目的は必須です'
      });
    } else if (scenario.goal.trim()) {
      // 長さチェック
      if (scenario.goal.length < validation.MIN_LENGTH) {
        errors.push({
          field: 'goal',
          message: `ロープレの目的は${validation.MIN_LENGTH}文字以上である必要があります`
        });
      }
      
      if (scenario.goal.length > validation.MAX_LENGTH) {
        errors.push({
          field: 'goal',
          message: `ロープレの目的は${validation.MAX_LENGTH}文字以下である必要があります`
        });
      }
    }
  }
  
  // 目安時間
  if (scenario.estimatedTime !== undefined) {
    const validation = SCENARIO_VALIDATIONS.ESTIMATED_TIME;
    
    // 必須チェック
    if (validation.REQUIRED && (!scenario.estimatedTime || scenario.estimatedTime <= 0)) {
      errors.push({
        field: 'estimatedTime',
        message: '目安時間は必須です'
      });
    } else if (scenario.estimatedTime) {
      // 数値チェック
      if (validation.ONLY_NUMERIC && (!Number.isInteger(scenario.estimatedTime) || scenario.estimatedTime <= 0)) {
        errors.push({
          field: 'estimatedTime',
          message: '目安時間は正の整数である必要があります'
        });
      }
      
      // ONLY_NUMERICの場合は値の上限・下限チェック
      if (validation.ONLY_NUMERIC && validation.MIN_VALUE !== undefined && scenario.estimatedTime < validation.MIN_VALUE) {
        errors.push({
          field: 'estimatedTime',
          message: `目安時間は${validation.MIN_VALUE}分以上である必要があります`
        });
      }
      
      if (validation.ONLY_NUMERIC && validation.MAX_VALUE !== undefined && scenario.estimatedTime > validation.MAX_VALUE) {
        errors.push({
          field: 'estimatedTime',
          message: `目安時間は${validation.MAX_VALUE}分以下である必要があります`
        });
      }
    }
  }
  
  // 状況設定
  if (scenario.situation !== undefined) {
    const validation = SCENARIO_VALIDATIONS.SITUATION;
    
    // 必須チェック
    if (validation.REQUIRED && !scenario.situation.trim()) {
      errors.push({
        field: 'situation',
        message: '状況設定は必須です'
      });
    } else if (scenario.situation.trim()) {
      // 長さチェック
      if (scenario.situation.length < validation.MIN_LENGTH) {
        errors.push({
          field: 'situation',
          message: `状況設定は${validation.MIN_LENGTH}文字以上である必要があります`
        });
      }
      
      if (scenario.situation.length > validation.MAX_LENGTH) {
        errors.push({
          field: 'situation',
          message: `状況設定は${validation.MAX_LENGTH}文字以下である必要があります`
        });
      }
    }
  }
  
  // 状況設定（クローズ）：非必須・2000文字上限
  if (scenario.situationClosed !== undefined) {
    const validation = SCENARIO_VALIDATIONS.SITUATION_CLOSED;
    if (scenario.situationClosed.length > validation.MAX_LENGTH) {
      errors.push({
        field: 'situationClosed',
        message: `状況設定（クローズ）は${validation.MAX_LENGTH}文字以下である必要があります`
      });
    }
  }
  
  // 相手役の名前
  if (scenario.targetName !== undefined) {
    const validation = SCENARIO_VALIDATIONS.TARGET_NAME;
    
    // 必須チェック
    if (validation.REQUIRED && !scenario.targetName.trim()) {
      errors.push({
        field: 'targetName',
        message: '相手役の名前は必須です'
      });
    } else if (scenario.targetName.trim()) {
      // 長さチェック
      if (scenario.targetName.length < validation.MIN_LENGTH) {
        errors.push({
          field: 'targetName',
          message: `相手役の名前は${validation.MIN_LENGTH}文字以上である必要があります`
        });
      }
      
      if (scenario.targetName.length > validation.MAX_LENGTH) {
        errors.push({
          field: 'targetName',
          message: `相手役の名前は${validation.MAX_LENGTH}文字以下である必要があります`
        });
      }
    }
  }
  
  // 所属
  if (scenario.department !== undefined) {
    const validation = SCENARIO_VALIDATIONS.DEPARTMENT;
    
    // 必須チェック
    if (validation.REQUIRED && !scenario.department.trim()) {
      errors.push({
        field: 'department',
        message: '所属は必須です'
      });
    } else if (scenario.department && scenario.department.trim()) {
      // 長さチェック
      if (scenario.department.length > validation.MAX_LENGTH) {
        errors.push({
          field: 'department',
          message: `所属は${validation.MAX_LENGTH}文字以下である必要があります`
        });
      }
      
      // 文字種チェック
      if (validation.ONLY_ALPHANUMERIC) {
        let pattern = '^[a-zA-Z0-9';
        if (validation.CAN_UNDERSCORE) pattern += '_';
        if (validation.CAN_HYPHEN) pattern += '-';
        if (validation.CAN_WHITESPACE) pattern += '\\s';
        pattern += ']+$';
        
        const regex = new RegExp(pattern);
        if (!regex.test(scenario.department)) {
          let allowedChars = '英数字';
          if (validation.CAN_UNDERSCORE) allowedChars += '、アンダースコア(_)';
          if (validation.CAN_HYPHEN) allowedChars += '、ハイフン(-)';
          if (validation.CAN_WHITESPACE) allowedChars += '、空白';
          
          errors.push({
            field: 'department',
            message: `所属は${allowedChars}のみ使用できます`
          });
        }
      }
    }
  }
  
  return {
    isValid: errors.length === 0,
    errors
  };
};

/**
 * カスタム評価ルール情報のバリデーションを行う
 * @param rule カスタム評価ルール情報
 * @returns バリデーション結果（成功時は空の配列、失敗時はエラーメッセージの配列）
 */
export const validateCustomEvaluationRule = (
  rule: {
    name?: string;
    content?: string;
  }
): { isValid: boolean; errors: { field: string; message: string }[] } => {
  const errors: { field: string; message: string }[] = [];
  
  // 名前
  if (rule.name !== undefined) {
    const validation = CUSTOM_EVALUATION_RULE_VALIDATIONS.NAME;
    
    // 必須チェック
    if (validation.REQUIRED && !rule.name.trim()) {
      errors.push({
        field: 'name',
        message: '名前は必須です'
      });
    } else if (rule.name.trim()) {
      // 長さチェック
      if (rule.name.length < validation.MIN_LENGTH) {
        errors.push({
          field: 'name',
          message: `名前は${validation.MIN_LENGTH}文字以上である必要があります`
        });
      }
      
      if (rule.name.length > validation.MAX_LENGTH) {
        errors.push({
          field: 'name',
          message: `名前は${validation.MAX_LENGTH}文字以下である必要があります`
        });
      }
    }
  }
  
  // 内容
  if (rule.content !== undefined) {
    const validation = CUSTOM_EVALUATION_RULE_VALIDATIONS.CONTENT;
    
    // 必須チェック
    if (validation.REQUIRED && !rule.content.trim()) {
      errors.push({
        field: 'content',
        message: '内容は必須です'
      });
    } else if (rule.content.trim()) {
      // 長さチェック
      if (rule.content.length < validation.MIN_LENGTH) {
        errors.push({
          field: 'content',
          message: `内容は${validation.MIN_LENGTH}文字以上である必要があります`
        });
      }
      
      if (rule.content.length > validation.MAX_LENGTH) {
        errors.push({
          field: 'content',
          message: `内容は${validation.MAX_LENGTH}文字以下である必要があります`
        });
      }
    }
  }
  
  return {
    isValid: errors.length === 0,
    errors
  };
};
